import { Component, OnInit, ChangeDetectorRef, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router'; // Added ActivatedRoute
import { NbLoginComponent, NbAuthService, NB_AUTH_OPTIONS, NbAuthResult } from '@nebular/auth';
import { NbTokenService, NbAuthToken, NbAuthJWTToken, NbAuthOAuth2JWTToken } from '@nebular/auth';
import { TokenService } from 'src/app/deebr/service/token.service';
import { TwoFactorService } from 'src/app/deebr/service/two-factor.service';

@Component({
  selector: 'app-deebr-login',
  templateUrl: './deebr-login.component.html',
  styleUrls: ['../auth.scss'],
  standalone: false
})
export class DeebrLoginComponent extends NbLoginComponent implements OnInit {

  show2FA = false;
  showSetup2FA = false;
  setupQrCode: string | null = null;
  verificationCode = '';
  trustDevice = false;
  tempToken = '';
  isAwaiting2FAActivation = false;

  constructor(
    service: NbAuthService,
    @Inject(NB_AUTH_OPTIONS) options: {},
    cd: ChangeDetectorRef,
    router: Router,
    private route: ActivatedRoute, // Injected ActivatedRoute
    private tokenService: TokenService,
    private nbTokenService: NbTokenService,
    private twoFactorService: TwoFactorService
  ) {
    super(service, options, cd, router);
  }

  ngOnInit(): void {
    // Check for 2FA activation success
    this.route.queryParams.subscribe(params => {
      if (params['reset'] === 'true') {
        this.messages = ["Votre double authentification a été désactivée. Veuillez vous reconnecter pour la reconfigurer."];
        this.cd.detectChanges();
      }
    });

    this.tokenService.verify().subscribe((isValid: boolean) => {
      if (!isValid) {
        this.router.navigate(['/auth/login']);
      }
    });
  }

  override login(): void {
    // Remove blocking check for isAwaiting2FAActivation to allow polling status

    this.errors = [];
    this.messages = [];
    this.submitted = true;

    this.service.authenticate(this.strategy, this.user).subscribe((result: NbAuthResult) => {
      this.submitted = false;

      const response = result.getResponse();
      const body = response?.body;

      // Check for 2FA flags FIRST, regardless of Nebular success state
      if (body && body['2fa_activation_pending']) {
        this.isAwaiting2FAActivation = true;
        this.showSetup2FA = false;
        this.tempToken = body['temp_token'];
        this.messages = ["Un email de confirmation vous a été envoyé. Veuillez cliquer sur le lien pour activer la double authentification."];
        this.cd.detectChanges();
        return;
      }

      if (body && body['2fa_required']) {
        this.show2FA = true;
        this.isAwaiting2FAActivation = false;
        this.tempToken = body['temp_token'];
        this.nbTokenService.clear().subscribe();
        this.cd.detectChanges();
        return;
      }

      if (body && body['2fa_setup_required']) {
        this.showSetup2FA = true;
        this.isAwaiting2FAActivation = false;
        this.tempToken = body['temp_token'];
        this.initiateSetup();
        this.cd.detectChanges();
        return;
      }

      if (result.isSuccess()) {
        this.messages = result.getMessages();
      } else {
        this.errors = result.getErrors();
      }

      const redirect = result.getRedirect();
      if (redirect && !this.show2FA && !this.showSetup2FA) {
        setTimeout(() => {
          return this.router.navigateByUrl(redirect);
        }, this.redirectDelay);
      }
      this.cd.detectChanges();
    });
  }

  initiateSetup() {
    // Token should be in storage/interceptor reach.
    this.twoFactorService.setup2FA(this.tempToken).subscribe({
      next: (res) => {
        this.setupQrCode = res.qr_code;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.errors = ['Erreur lors de la génération du QR Code'];
        this.cd.detectChanges();
        // If error (e.g. 401), we might need to login again
        this.nbTokenService.clear().subscribe();
      }
    });
  }

  confirmSetup() {
    if (!this.verificationCode) return;
    this.submitted = true;

    this.twoFactorService.confirm2FA(this.tempToken, this.verificationCode).subscribe({
      next: (res) => {
        if (res.status === 'email_sent') {
          this.submitted = false; // Allow messages to be shown
          this.isAwaiting2FAActivation = true;
          this.errors = [];
          this.messages = ["Un email de confirmation vient d'être envoyé. Veuillez cliquer sur le lien pour activer la double authentification."];
          this.showSetup2FA = false;
          this.cd.detectChanges();
        } else {
          // Fallback if logic changes (e.g. direct activation)
          this.verify2FA();
        }
      },
      error: (err) => {
        this.submitted = false;
        this.errors = ['Code invalide.'];
        this.cd.detectChanges();
      }
    });
  }

  retryActivation() {
    this.twoFactorService.resendActivationEmail(this.tempToken).subscribe({
      next: () => {
        this.errors = [];
        this.messages = ["Nous vous avons renvoyé un email, vérifiez dans vos spams et si vous n'avez rien reçu, veuillez contacter votre chargé de compte Deebr."];
        this.cd.detectChanges();
      },
      error: (err) => {
        this.errors = ["Impossible de renvoyer l'email."];
        this.cd.detectChanges();
      }
    });
  }

  verify2FA() {
    if (!this.verificationCode) return;
    this.submitted = true;

    this.twoFactorService.verify2FA(this.tempToken, this.verificationCode, this.trustDevice)
      .subscribe({
        next: (res) => {
          this.submitted = false;
          const payload = {
            access_token: res.access,
            refresh_token: res.refresh,
          };
          const token = new NbAuthOAuth2JWTToken(payload, 'email', new Date());
          this.nbTokenService.set(token).subscribe(() => {
            this.router.navigate(['/pages/dashboard']);
          });
        },
        error: (err) => {
          this.submitted = false;
          console.error('Verify 2FA Error:', err);
          if (err.error && err.error.error) {
            this.errors = [err.error.error];
          } else {
            this.errors = ['Code 2FA invalide ou expiré'];
          }
          this.cd.detectChanges();
        }
      });
  }

  lostCode() {
    if (!this.tempToken) return;

    this.twoFactorService.requestReset(this.tempToken).subscribe({
      next: () => {
        this.messages = ['Un email de réinitialisation vous a été envoyé.'];
        this.show2FA = false;
        this.user = { rememberMe: false, username: '', password: '' }; // Reset form
        this.cd.detectChanges();
      },
      error: (err) => {
        this.errors = ["Impossible d'envoyer l'email."];
        this.cd.detectChanges();
      }
    });
  }
}
